import React from 'react';
import { BlogLayout, H2, H3, P, UL, InfoBox, ScoreCard } from './BlogLayout';

const Blog1 = () => (
  <BlogLayout
    title="How to Check If Your Website Is Secure"
    description="A complete step-by-step guide to checking your website's security — from SSL certificates to malware scans. No technical knowledge required."
    date="April 2026"
    readTime={7}
    category="Security Basics"
  >
    <P>
      If you own a website — a business site, a blog, an e-commerce store — security isn't optional. A single vulnerability can get your site blacklisted by Google, compromise your visitors' data, or hand your entire server over to attackers. The good news: checking your security doesn't require a degree in computer science.
    </P>
    <P>
      This guide walks you through exactly how to check if your website is secure, what to look for, and what to do when you find a problem.
    </P>

    <H2>Step 1 — Check the Padlock (SSL Certificate)</H2>
    <P>
      The first thing to check is whether your site runs over HTTPS. Open your website in any browser and look at the address bar. You should see a padlock icon before your URL.
    </P>
    <InfoBox title="What the padlock means" color="#00FF94">
      A padlock means your site has a valid SSL/TLS certificate. All data between your visitor and your server is encrypted. Without it, anyone on the same WiFi network can read what your visitors type — including passwords and payment details.
    </InfoBox>
    <P>Click the padlock. It should say "Connection is secure" and show a valid certificate. Check:</P>
    <UL items={[
      'Certificate is issued to your domain (not a random organisation)',
      'Certificate has not expired — check the expiry date',
      "Certificate is issued by a trusted authority (Let's Encrypt, DigiCert, etc.)",
      'Your entire site loads over https:// — not just the homepage',
    ]} />
    <P>
      If you see "Not Secure" or a broken padlock, this is an urgent fix. Google actively penalises HTTP-only sites in search rankings, and modern browsers warn visitors before they even land on your page.
    </P>

    <H2>Step 2 — Check Your HTTP Security Headers</H2>
    <P>
      Security headers are invisible instructions your server sends to the browser. They tell the browser how to behave — whether to allow your site to be embedded in other pages, whether to enforce HTTPS, whether to block certain types of attacks.
    </P>
    <P>The six most important ones are:</P>
    <ScoreCard label="Content-Security-Policy (CSP)" score="Prevents XSS attacks" color="#00FF94" />
    <ScoreCard label="Strict-Transport-Security (HSTS)" score="Forces HTTPS" color="#00FF94" />
    <ScoreCard label="X-Frame-Options" score="Prevents clickjacking" color="#00FF94" />
    <ScoreCard label="X-Content-Type-Options" score="Prevents MIME sniffing" color="#FFD600" />
    <ScoreCard label="Referrer-Policy" score="Controls data leakage" color="#FFD600" />
    <ScoreCard label="Permissions-Policy" score="Limits browser features" color="#FF0055" />
    <P>
      Most websites — even professional ones — are missing 3 or 4 of these. You can check yours by running a WebShield scan, which instantly tells you which headers are present, which are missing, and what risk each gap creates.
    </P>

    <H2>Step 3 — Check for Open Ports</H2>
    <P>
      Your server communicates with the internet through numbered "ports". Port 80 is HTTP, port 443 is HTTPS, port 22 is SSH. Every open port that isn't necessary is an attack surface.
    </P>
    <InfoBox title="Common risky open ports" color="#FF0055">
      Port 3306 (MySQL), Port 27017 (MongoDB), Port 6379 (Redis) — these database ports should never be open to the public internet. If they are, attackers can attempt to connect directly to your database without ever touching your website.
    </InfoBox>
    <UL items={[
      'Only port 443 (HTTPS) and 80 (HTTP redirect) should be publicly open on most websites',
      'Port 22 (SSH) should only be open to specific IP addresses, not the whole internet',
      'Any database port being open to the public is a critical vulnerability',
      'Port scan your server monthly — new software installations can accidentally open ports',
    ]} />

    <H2>Step 4 — Check Your DNS Configuration</H2>
    <P>
      DNS isn't just about pointing your domain to your server. Properly configured DNS records also protect your email from being spoofed and your domain from being hijacked.
    </P>
    <H3>SPF Record</H3>
    <P>
      SPF (Sender Policy Framework) tells email servers which servers are allowed to send email from your domain. Without it, anyone can send phishing emails that appear to come from your domain — putting your brand reputation at serious risk.
    </P>
    <H3>DMARC Record</H3>
    <P>
      DMARC builds on SPF and DKIM to tell receiving mail servers what to do when an email fails authentication. Without DMARC, even if you have SPF set up, spoofed emails still reach inboxes.
    </P>

    <H2>Step 5 — Check Google's Blacklist</H2>
    <P>
      Google Safe Browsing maintains a list of websites that host malware, run phishing scams, or show deceptive content. If your site gets listed, Google Chrome shows a full-page red warning before any visitor can access your site. This destroys traffic instantly.
    </P>
    <P>
      You can check if your site is blacklisted at <strong style={{ color: '#00F0FF' }}>transparencyreport.google.com/safe-browsing/search</strong> — just type your domain. A WebShield scan checks this automatically.
    </P>

    <H2>Step 6 — Check Your Cookie Security</H2>
    <P>
      If your website uses cookies — for sessions, logins, shopping carts, or analytics — each cookie should have three security attributes:
    </P>
    <UL items={[
      'Secure flag — cookie is only sent over HTTPS, never plain HTTP',
      'HttpOnly flag — cookie cannot be accessed by JavaScript (prevents XSS theft)',
      'SameSite attribute — prevents the cookie being sent in cross-site requests (CSRF protection)',
    ]} />

    <H2>How to Run All These Checks at Once</H2>
    <P>
      Manually checking each of these takes hours and requires tools like curl, nmap, and dig. WebShield runs all 8 checks in under 60 seconds and gives you a security score, letter grade, and prioritised list of exactly what to fix first.
    </P>
    <InfoBox title="What WebShield checks automatically" color="#7000FF">
      SSL certificate validity and expiry · HTTP security headers · Open port scan · DNS configuration (SPF, DMARC, MX) · Technology stack exposure · Google malware / blacklist check · Response performance · Cookie security flags
    </InfoBox>
    <P>
      After running your scan, you get a PDF report emailed to you with a score out of 100 and specific recommendations broken into HIGH, MEDIUM, and LOW priority. Share it with your developer or hosting provider and ask them to fix the HIGH priority items first.
    </P>
  </BlogLayout>
);

export default Blog1;